import type { CSSObject } from "@emotion/react";
import type { SxProps, Theme } from "@mui/material/styles";

import { multiSectionDigitalClockSectionClasses } from "@mui/x-date-pickers/MultiSectionDigitalClock";

export const MUI_FILLED_FIELD = {
  backgroundDefault: "var(--color-background-default)",
  borderAccent: "var(--color-border-accent)",
  borderFocus: "var(--color-border-focus)",
  borderHover: "var(--color-border-hover)",
  borderStrong: "var(--color-border-strong)",
  borderSubtle: "var(--color-border-subtle)",
  elementPrimary: "var(--color-element-primary-initial)",
  elementPrimaryHover: "var(--color-element-primary-hover)",
  elementPrimaryOn: "var(--color-element-primary-on-primary)",
  elevation100: "var(--box-shadow-elevation-100)",
  fgBrand: "var(--color-foreground-brand)",
  fgDisabled: "var(--color-foreground-disabled)",
  fgMuted: "var(--color-foreground-muted)",
  fgMutedSubtle: "var(--color-foreground-muted-subtle)",
  fgPrimary: "var(--color-foreground-primary)",
  fgSecondary: "var(--color-foreground-secondary)",
  fgStrong: "var(--color-foreground-strong)",
  selectionStrong: "var(--color-selection-strong)",
  selectionSubtle: "var(--color-selection-subtle)",
  statusNegative: "var(--color-purpose-status-negative)",
  surfaceBright: "var(--color-surface-bright)",
} as const;

const MUI_FILLED_INPUT_AUTOFILL = {
  "input:-webkit-autofill,input:-webkit-autofill:hover,input:-webkit-autofill:focus,input:-webkit-autofill:active": {
    transition: "background-color 720000s ease-in-out 0s",
  },
} as const;

export const muiFilledInputLabelClasses = {
  root: "capitalize translate-x-3 overflow-visible font-ds-label-lg !tracking-wide !text-lds-foreground-muted-subtle [&.Mui-focused]:!text-lds-foreground-brand [&:not(.MuiInputLabel-shrink)]:translate-y-[1.3rem] [&:not(.MuiInputLabel-shrink)]:scale-95",
  shrink: "text-lds-foreground-secondary translate-y-1 scale-[75%]",
} as const;

export type FilledTextFieldStyleParams = {
  displayshrinkedlabel?: string;
  error?: boolean;
  height?: string;
  labelcolor?: string;
  textcolor?: string;
};

type MuiFilledInputRootStyleOptions = {
  borderColor?: string;
  disabledTextColor?: string;
  error?: boolean;
  focusBorder?: "accent" | "focus";
  height?: string;
  hoverBorder?: boolean;
  includeActiveState?: boolean;
  textColor?: string;
};

export function buildMuiFilledInputRootStyles({
  borderColor = MUI_FILLED_FIELD.borderStrong,
  disabledTextColor = MUI_FILLED_FIELD.fgDisabled,
  error = false,
  focusBorder = "focus",
  height,
  hoverBorder = true,
  includeActiveState = false,
  textColor,
}: MuiFilledInputRootStyleOptions = {}) {
  const borderBase = error ? MUI_FILLED_FIELD.statusNegative : borderColor;
  const borderFocus = error
    ? MUI_FILLED_FIELD.statusNegative
    : MUI_FILLED_FIELD[focusBorder === "accent" ? "borderAccent" : "borderFocus"];

  const hoverStyles = hoverBorder
    ? {
        ":before": { border: "none !important" },
        backgroundColor: MUI_FILLED_FIELD.surfaceBright,
        border: `1px solid ${MUI_FILLED_FIELD.borderHover}`,
        boxShadow: MUI_FILLED_FIELD.elevation100,
      }
    : { boxShadow: MUI_FILLED_FIELD.elevation100 };

  return {
    "&.Mui-disabled": {
      "&:before": { border: "none" },
      backgroundColor: MUI_FILLED_FIELD.surfaceBright,
      border: `1px solid ${MUI_FILLED_FIELD.borderSubtle}`,
      color: disabledTextColor,
      WebkitTextFillColor: disabledTextColor,
    },
    "&.Mui-disabled:hover": {
      backgroundColor: MUI_FILLED_FIELD.surfaceBright,
      border: `1px solid ${MUI_FILLED_FIELD.borderSubtle}`,
    },
    "&.Mui-focused:not(.Mui-disabled)": {
      backgroundColor: MUI_FILLED_FIELD.surfaceBright,
      border: `1px solid ${borderFocus}`,
    },
    ...(includeActiveState
      ? {
          "&:active:not(.Mui-disabled)": {
            border: `1px solid ${error ? MUI_FILLED_FIELD.statusNegative : MUI_FILLED_FIELD.borderAccent}`,
          },
        }
      : {}),
    "&:after": { border: "none" },
    "&:before": { border: "none" },
    "&:hover:not(.Mui-disabled)": hoverStyles,
    backgroundColor: MUI_FILLED_FIELD.surfaceBright,
    border: `1px solid ${borderBase}`,
    borderRadius: "7px",
    ...(height ? { height } : {}),
    ...(textColor ? { color: textColor, letterSpacing: "0rem" } : {}),
  };
}

export function getMuiFilledTextFieldSx(options?: MuiFilledInputRootStyleOptions): SxProps<Theme> {
  return {
    "& .MuiInputBase-input.MuiFilledInput-input": {
      "&:before, &:after": {
        border: "none",
      },
      transform: "translate(0rem, -0.8rem)",
    },
    "& .MuiInputBase-root.MuiFilledInput-root": buildMuiFilledInputRootStyles({
      focusBorder: "accent",
      hoverBorder: false,
      includeActiveState: true,
      ...options,
    }),
    ...MUI_FILLED_INPUT_AUTOFILL,
    width: "100%",
  };
}

export function getTimePickerModalSx(): SxProps<Theme> {
  return {
    [`&& .${multiSectionDigitalClockSectionClasses.item}`]: {
      "&.Mui-focusVisible:not(.Mui-selected)": {
        backgroundColor: MUI_FILLED_FIELD.selectionSubtle,
      },
      "&.Mui-selected": {
        "&:focus-visible, &:hover": {
          backgroundColor: MUI_FILLED_FIELD.elementPrimaryHover,
          color: MUI_FILLED_FIELD.elementPrimaryOn,
        },
        backgroundColor: MUI_FILLED_FIELD.elementPrimary,
        color: MUI_FILLED_FIELD.elementPrimaryOn,
      },
      "&:hover:not(.Mui-selected)": {
        backgroundColor: MUI_FILLED_FIELD.selectionSubtle,
      },
    },
  };
}

export function muiFilledTextFieldStyles(
  p: FilledTextFieldStyleParams,
  options: { withFloatingLabel: boolean }
): CSSObject {
  const filledRoot = {
    "& .MuiInputBase-root.MuiFilledInput-root": buildMuiFilledInputRootStyles({
      error: Boolean(p.error),
      height: p.height,
      hoverBorder: false,
      includeActiveState: true,
      textColor: p.textcolor,
    }),
  };

  const inputSlot = {
    "& .MuiInputBase-input.MuiFilledInput-input": {
      "&:before, &:after": {
        border: "none",
      },
      transform: p.displayshrinkedlabel === "true" ? "" : "translate(0rem, -0.8rem)",
    },
  };

  const shrink = {
    "& .MuiInputLabel-shrink": {
      display: p.displayshrinkedlabel === "true" ? "block" : "none",
    },
  };

  const floatingLabel = options.withFloatingLabel
    ? {
        "& .MuiFormLabel-root.MuiInputLabel-root.Mui-focused": {
          color: p.labelcolor,
        },
        "& .MuiInputLabel-root.Mui-disabled": {
          color: MUI_FILLED_FIELD.fgDisabled,
        },
      }
    : {};

  return {
    ...floatingLabel,
    ...inputSlot,
    ...filledRoot,
    ...shrink,
    ...MUI_FILLED_INPUT_AUTOFILL,
  } as unknown as CSSObject;
}
